import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "@phosphor-icons/react";
import { Input, Button, Switch } from "../../components/ui";
import FieldRenderer from "./FieldRenderer";
import { SECTION_TYPES, typeLabel } from "./sectionTypes";

export default function SectionEditor({ open, section, onClose, onSave, saving }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState({});
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (section) {
      setTitle(section.title || "");
      setContent(section.content || {});
      setIsActive(section.is_active ?? true);
    }
  }, [section]);

  const blueprint = section ? SECTION_TYPES[section.type] : null;
  const setField = (name, v) => setContent((c) => ({ ...c, [name]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ title, is_active: isActive, content });
  };

  return (
    <AnimatePresence>
      {open && section && blueprint && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex h-full w-full max-w-lg flex-col border-l border-line bg-surface shadow-pop"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-ink">{typeLabel(section.type)}</h2>
                <p className="text-xs text-muted">{blueprint.description}</p>
              </div>
              <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <form id="section-form" onSubmit={handleSubmit} className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
              <div className="flex items-center justify-between rounded-xl border border-line bg-surface-2 px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-ink">Visible on the store</div>
                  <div className="text-xs text-muted">Hide without deleting.</div>
                </div>
                <Switch checked={isActive} onChange={setIsActive} />
              </div>

              <Input label="Label (internal)" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`e.g. ${typeLabel(section.type)}`} hint="Only shown here in the admin." />

              <div className="space-y-5 border-t border-line pt-5">
                {blueprint.fields.map((field) => (
                  <FieldRenderer key={field.name} field={field} value={content[field.name]} onChange={(v) => setField(field.name, v)} />
                ))}
              </div>
            </form>

            <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" form="section-form" loading={saving}>Save section</Button>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
